/**
 * Edit Group Dialog Component
 * Dialog for editing existing group information including avatar and tags
 */

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Slider,
  Box,
  IconButton,
  Alert,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  LocalOffer as LocalOfferIcon,
  Lock as LockIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material'
import { ImageUploadZone } from '@/components/common/ImageUploadZone'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { socialApi } from '@api/endpoints/social'
import { ApiService } from '@api/apiService'
import { useCachedTags } from '@/hooks/useCachedTags'
import { TagType } from '@/features/adventures/types/adventure-editor.types'
import type { GroupDetailsDto, UpdateGroupRequest } from '@api/types/social'
import { GroupVisibility, getGroupVisibilityName, getGroupVisibilityDescription, GroupPermissionLevel } from '@api/types/social'
import { getPermissionLevelName, getPermissionLevelDescription, permissionDescriptions } from '@utils/groupPermissions'
import { GeofencePicker } from '@/components/map/GeofencePicker'

interface EditGroupDialogProps {
  open: boolean
  onClose: () => void
  group: GroupDetailsDto
}

export function EditGroupDialog({ open, onClose, group }: EditGroupDialogProps) {
  const queryClient = useQueryClient()
  const { getCategoriesByType, isLoading: isLoadingTags } = useCachedTags()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description || '')
  const [visibility, setVisibility] = useState<GroupVisibility>(
    group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)
  )
  const [maxMembers, setMaxMembers] = useState(group.maxMembers || 50)
  const [groupImage, setGroupImage] = useState<string | null>(group.groupImage || null)
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null)
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    group.expirationDate ? new Date(group.expirationDate) : null
  )
  const [selectedInterestTagIds, setSelectedInterestTagIds] = useState<string[]>([])
  const [selectedLookingForTagIds, setSelectedLookingForTagIds] = useState<string[]>([])
  const [selectedAdventureTypeTagIds, setSelectedAdventureTypeTagIds] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [expandedInterestCategory, setExpandedInterestCategory] = useState<string | null>(null)
  const [expandedLookingForCategory, setExpandedLookingForCategory] = useState<string | null>(null)
  const [expandedAdventureTypeCategory, setExpandedAdventureTypeCategory] = useState<string | null>(null)

  // Geofence state
  const [geofenceEnabled, setGeofenceEnabled] = useState(false)
  const [geofenceCenter, setGeofenceCenter] = useState<{ latitude: number; longitude: number } | undefined>(undefined)
  const [geofenceRadiusMiles, setGeofenceRadiusMiles] = useState<number>(10)

  // Permission settings state
  const [viewMembersLevel, setViewMembersLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.viewMembersLevel ?? GroupPermissionLevel.Anyone
  )
  const [inviteUsersLevel, setInviteUsersLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.inviteUsersLevel ?? GroupPermissionLevel.Moderators
  )
  const [viewTagsLevel, setViewTagsLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.viewTagsLevel ?? GroupPermissionLevel.Anyone
  )
  const [createDiscussionsLevel, setCreateDiscussionsLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.createDiscussionsLevel ?? GroupPermissionLevel.Members
  )
  const [participateInDiscussionsLevel, setParticipateInDiscussionsLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.participateInDiscussionsLevel ?? GroupPermissionLevel.Members
  )
  const [viewDiscussionsLevel, setViewDiscussionsLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.viewDiscussionsLevel ?? GroupPermissionLevel.Anyone
  )
  const [accessChatLevel, setAccessChatLevel] = useState<GroupPermissionLevel>(
    group.permissionSettings?.accessChatLevel ?? GroupPermissionLevel.Members
  )

  // Tab navigation state
  const [currentTab, setCurrentTab] = useState(0)

  // Get tag categories
  const interestCategories = getCategoriesByType(TagType.Interests)
  const lookingForCategories = getCategoriesByType(TagType.LookingFor)
  const adventureTypeCategories = getCategoriesByType(TagType.AdventureTypes)

  // Get all tags from categories
  const interestTags = interestCategories.flatMap(cat => cat.tags || [])
  const lookingForTags = lookingForCategories.flatMap(cat => cat.tags || [])
  const adventureTypeTags = adventureTypeCategories.flatMap(cat => cat.tags || [])

  // Reset form when dialog opens with new group data
  useEffect(() => {
    if (open) {
      setName(group.name)
      setDescription(group.description || '')
      setVisibility(group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly))
      setMaxMembers(group.maxMembers || 50)
      setGroupImage(group.groupImage || null)
      setUploadedImageFile(null)
      setExpirationDate(group.expirationDate ? new Date(group.expirationDate) : null)
      setErrors({})
      // Populate selected tags from group data
      setSelectedInterestTagIds(group.interestTags?.map(t => t.id) || [])
      setSelectedLookingForTagIds(group.lookingForTags?.map(t => t.id) || [])
      setSelectedAdventureTypeTagIds(group.adventureTypeTags?.map(t => t.id) || [])
      // Reset permission settings
      setViewMembersLevel(group.permissionSettings?.viewMembersLevel ?? GroupPermissionLevel.Anyone)
      setInviteUsersLevel(group.permissionSettings?.inviteUsersLevel ?? GroupPermissionLevel.Moderators)
      setViewTagsLevel(group.permissionSettings?.viewTagsLevel ?? GroupPermissionLevel.Anyone)
      setCreateDiscussionsLevel(group.permissionSettings?.createDiscussionsLevel ?? GroupPermissionLevel.Members)
      setParticipateInDiscussionsLevel(group.permissionSettings?.participateInDiscussionsLevel ?? GroupPermissionLevel.Members)
      setViewDiscussionsLevel(group.permissionSettings?.viewDiscussionsLevel ?? GroupPermissionLevel.Anyone)
      setAccessChatLevel(group.permissionSettings?.accessChatLevel ?? GroupPermissionLevel.Members)
      // Reset geofence settings
      const hasGeofence = group.geofenceCenterLatitude != null && group.geofenceCenterLongitude != null && group.geofenceRadiusMiles != null
      setGeofenceEnabled(hasGeofence)
      setGeofenceCenter(
        hasGeofence && group.geofenceCenterLatitude != null && group.geofenceCenterLongitude != null
          ? { latitude: group.geofenceCenterLatitude, longitude: group.geofenceCenterLongitude }
          : undefined
      )
      setGeofenceRadiusMiles(group.geofenceRadiusMiles || 10)
      // Reset to first tab
      setCurrentTab(0)
    }
  }, [open, group])

  // Handle image upload
  const handleImageSelect = (file: File) => {
    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setGroupImage(reader.result as string)
      setUploadedImageFile(file)
      setErrors(prev => ({ ...prev, groupImage: '' }))
    }
    reader.readAsDataURL(file)
  }

  const handleToggleInterestTag = (tagId: string) => {
    setSelectedInterestTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const handleToggleLookingForTag = (tagId: string) => {
    setSelectedLookingForTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const handleToggleAdventureTypeTag = (tagId: string) => {
    setSelectedAdventureTypeTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Group name is required'
    } else if (name.length < 3) {
      newErrors.name = 'Group name must be at least 3 characters'
    } else if (name.length > 100) {
      newErrors.name = 'Group name must be less than 100 characters'
    }

    if (description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters'
    }

    if (expirationDate && expirationDate <= new Date()) {
      newErrors.expirationDate = 'Expiration date must be in the future'
    }

    // Validate geofence if enabled
    if (geofenceEnabled) {
      if (!geofenceCenter) {
        newErrors.geofence = 'Please select a location on the map for the geofence center'
      }

      if (geofenceRadiusMiles <= 0) {
        newErrors.geofenceRadius = 'Radius must be greater than 0'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const updateGroupMutation = useMutation({
    mutationFn: async (data: { groupUpdate: UpdateGroupRequest }) => {
      // Update basic group info (including permissions as nested object)
      const updatedGroup = await socialApi.updateGroup(group.id, data.groupUpdate)

      // If there's an image file to upload, upload it after update
      if (uploadedImageFile) {
        const formData = new FormData()
        formData.append('file', uploadedImageFile)
        await ApiService.upload(`/api/groups/${group.id}/image`, formData)
      }

      return updatedGroup
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', group.id] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      onClose()
    },
    onError: (err) => {
      setErrors(prev => ({
        ...prev,
        submit: err instanceof Error ? err.message : 'Failed to update group'
      }))
    },
  })

  const handleSubmit = () => {
    if (!validate()) return

    const groupUpdate: UpdateGroupRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      visibility,
      maxMembers: maxMembers > 0 ? maxMembers : undefined,
      // Only include existing groupImage if we're NOT uploading a new file
      // (new file upload is handled separately after the update)
      groupImage: !uploadedImageFile ? (groupImage || undefined) : undefined,
      interestTagIds: selectedInterestTagIds.length > 0 ? selectedInterestTagIds : undefined,
      lookingForTagIds: selectedLookingForTagIds.length > 0 ? selectedLookingForTagIds : undefined,
      adventureTypeTagIds: selectedAdventureTypeTagIds.length > 0 ? selectedAdventureTypeTagIds : undefined,
      expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
      permissionSettings: {
        viewMembersLevel,
        inviteUsersLevel,
        viewTagsLevel,
        createDiscussionsLevel,
        participateInDiscussionsLevel,
        viewDiscussionsLevel,
        accessChatLevel,
      },
      geofenceCenterLatitude: geofenceEnabled && geofenceCenter ? geofenceCenter.latitude : null,
      geofenceCenterLongitude: geofenceEnabled && geofenceCenter ? geofenceCenter.longitude : null,
      geofenceRadiusMiles: geofenceEnabled ? geofenceRadiusMiles : null,
    }

    updateGroupMutation.mutate({ groupUpdate })
  }

  // Get chip style
  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Edit Group</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Tabs Navigation */}
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Appearance" icon={<ImageIcon />} iconPosition="start" />
          <Tab label="Tags" icon={<LocalOfferIcon />} iconPosition="start" />
          <Tab label="Permissions" icon={<LockIcon />} iconPosition="start" />
          <Tab label="Location" icon={<LocationOnIcon />} iconPosition="start" />
        </Tabs>

        {/* Error Alert - Always Visible */}
        {errors.submit && (
          <Alert severity="error" onClose={() => setErrors(prev => ({ ...prev, submit: '' }))} sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Tab 0: General Settings */}
          {currentTab === 0 && (
            <>
              {/* Basic Info Section */}
              <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Group Name"
                required
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name || `${name.length}/100`}
                inputProps={{ maxLength: 100 }}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={!!errors.description}
                helperText={errors.description || `${description.length}/500`}
                inputProps={{ maxLength: 500 }}
              />

              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                  Group Visibility
                </FormLabel>
                <RadioGroup
                  value={visibility}
                  onChange={(e) => setVisibility(Number(e.target.value) as GroupVisibility)}
                >
                  {[GroupVisibility.Public, GroupVisibility.MatchingTagRequired, GroupVisibility.InviteOnly].map((vis) => (
                    <Box
                      key={vis}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: visibility === vis ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        backgroundColor: visibility === vis ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => setVisibility(vis)}
                    >
                      <FormControlLabel
                        value={vis}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: visibility === vis ? 600 : 400 }}>
                              {getGroupVisibilityName(vis)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getGroupVisibilityDescription(vis)}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: '100%' }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Max Members: {maxMembers}
                </Typography>
                <Slider
                  value={maxMembers}
                  onChange={(_, value) => setMaxMembers(value as number)}
                  min={1}
                  max={500}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 100, label: '100' },
                    { value: 250, label: '250' },
                    { value: 500, label: '500' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          </Box>
            </>
          )}

          {/* Tab 1: Appearance */}
          {currentTab === 1 && (
            <>
              {/* Image Upload Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Group Avatar
                </Typography>
                <ImageUploadZone
                  value={groupImage}
                  onFileSelect={handleImageSelect}
                  onClear={() => {
                    setGroupImage(null)
                    setUploadedImageFile(null)
                    setErrors(prev => ({ ...prev, groupImage: '' }))
                  }}
                  maxSizeMB={5}
                  label="Upload Group Avatar"
                  description="Drag and drop, paste, or click to select"
                  isUploading={updateGroupMutation.isPending}
                  error={errors.groupImage}
                  allowCamera={true}
                  disabled={updateGroupMutation.isPending}
                  aspectRatio="1:1"
                  cameraMode="user"
                />
              </Box>
            </>
          )}

          {/* Tab 0: General Settings - Expiration Date */}
          {currentTab === 0 && (
            <>
              {/* Expiration Date Section */}
              <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Expiration Date (Optional)
            </Typography>
            <Box sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Group Expiration Date"
                  value={expirationDate}
                  onChange={(newValue) => setExpirationDate(newValue)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.expirationDate,
                      helperText: errors.expirationDate || 'When should this group automatically disband?',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
              </Box>
            </>
          )}

          {/* Tab 2: Tags */}
          {currentTab === 2 && (
            <>
          {/* Tags Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Tags & Interests
            </Typography>

            {/* Selected Tags Summary */}
            {(selectedInterestTagIds.length > 0 ||
              selectedLookingForTagIds.length > 0 ||
              selectedAdventureTypeTagIds.length > 0) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Selected Tags ({selectedInterestTagIds.length + selectedLookingForTagIds.length + selectedAdventureTypeTagIds.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {selectedInterestTagIds.map((tagId) => {
                    const tag = interestTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = interestCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleInterestTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                  {selectedLookingForTagIds.map((tagId) => {
                    const tag = lookingForTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = lookingForCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleLookingForTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                  {selectedAdventureTypeTagIds.map((tagId) => {
                    const tag = adventureTypeTags.find(t => t.id === tagId)
                    if (!tag) return null
                    const category = adventureTypeCategories.find(c => c.tags?.some(t => t.id === tagId))
                    return (
                      <Chip
                        key={tagId}
                        label={tag.displayName}
                        size="small"
                        onDelete={() => handleToggleAdventureTypeTag(tagId)}
                        sx={{ backgroundColor: category?.color, color: 'white' }}
                      />
                    )
                  })}
                </Box>
              </Paper>
            )}

            {/* Interest Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Interest Tags
              </Typography>
              {isLoadingTags ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                interestCategories.map((category) => (
                  <Accordion
                    key={category.id}
                    expanded={expandedInterestCategory === category.id}
                    onChange={(_, isExpanded) =>
                      setExpandedInterestCategory(isExpanded ? category.id : null)
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>
                          {category.icon} {category.displayName}
                        </Typography>
                        <Chip
                          label={selectedInterestTagIds.filter(id =>
                            category.tags?.some(t => t.id === id)
                          ).length}
                          size="small"
                          sx={{ backgroundColor: category.color, color: 'white' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category.tags?.filter(t => t.isActive).map((tag) => {
                          const isSelected = selectedInterestTagIds.includes(tag.id)
                          return (
                            <Chip
                              key={tag.id}
                              label={
                                <>
                                  {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                  {tag.displayName}
                                </>
                              }
                              onClick={() => handleToggleInterestTag(tag.id)}
                              variant={isSelected ? 'filled' : 'outlined'}
                              size="small"
                              sx={getTagChipStyle(category.color, isSelected)}
                              title={tag.description}
                            />
                          )
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>

            {/* Looking For Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Looking For Tags
              </Typography>
              {isLoadingTags ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                lookingForCategories.map((category) => (
                  <Accordion
                    key={category.id}
                    expanded={expandedLookingForCategory === category.id}
                    onChange={(_, isExpanded) =>
                      setExpandedLookingForCategory(isExpanded ? category.id : null)
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>
                          {category.icon} {category.displayName}
                        </Typography>
                        <Chip
                          label={selectedLookingForTagIds.filter(id =>
                            category.tags?.some(t => t.id === id)
                          ).length}
                          size="small"
                          sx={{ backgroundColor: category.color, color: 'white' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category.tags?.filter(t => t.isActive).map((tag) => {
                          const isSelected = selectedLookingForTagIds.includes(tag.id)
                          return (
                            <Chip
                              key={tag.id}
                              label={
                                <>
                                  {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                  {tag.displayName}
                                </>
                              }
                              onClick={() => handleToggleLookingForTag(tag.id)}
                              variant={isSelected ? 'filled' : 'outlined'}
                              size="small"
                              sx={getTagChipStyle(category.color, isSelected)}
                              title={tag.description}
                            />
                          )
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>

            {/* Adventure Type Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adventure Type Tags
              </Typography>
              {isLoadingTags ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                adventureTypeCategories.map((category) => (
                  <Accordion
                    key={category.id}
                    expanded={expandedAdventureTypeCategory === category.id}
                    onChange={(_, isExpanded) =>
                      setExpandedAdventureTypeCategory(isExpanded ? category.id : null)
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>
                          {category.icon} {category.displayName}
                        </Typography>
                        <Chip
                          label={selectedAdventureTypeTagIds.filter(id =>
                            category.tags?.some(t => t.id === id)
                          ).length}
                          size="small"
                          sx={{ backgroundColor: category.color, color: 'white' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category.tags?.filter(t => t.isActive).map((tag) => {
                          const isSelected = selectedAdventureTypeTagIds.includes(tag.id)
                          return (
                            <Chip
                              key={tag.id}
                              label={
                                <>
                                  {tag.icon && <span style={{ marginRight: 4 }}>{tag.icon}</span>}
                                  {tag.displayName}
                                </>
                              }
                              onClick={() => handleToggleAdventureTypeTag(tag.id)}
                              variant={isSelected ? 'filled' : 'outlined'}
                              size="small"
                              sx={getTagChipStyle(category.color, isSelected)}
                              title={tag.description}
                            />
                          )
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </Box>
            </>
          )}

          {/* Tab 3: Permissions */}
          {currentTab === 3 && (
            <>
          {/* Permission Settings Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🔒 Permission Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control who can perform different actions within your group
            </Typography>

            <Paper variant="outlined" sx={{ p: 2 }}>
              {/* View Members Permission */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>View Members</Typography>
                    <Chip label={getPermissionLevelName(viewMembersLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.viewMembers}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={viewMembersLevel}
                      onChange={(e) => setViewMembersLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                        GroupPermissionLevel.Moderators,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* Invite Users Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>Invite Users</Typography>
                    <Chip label={getPermissionLevelName(inviteUsersLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.inviteUsers}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={inviteUsersLevel}
                      onChange={(e) => setInviteUsersLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                        GroupPermissionLevel.Moderators,
                        GroupPermissionLevel.Admins,
                        GroupPermissionLevel.Owner,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* View Tags Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>View Tags</Typography>
                    <Chip label={getPermissionLevelName(viewTagsLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.viewTags}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup value={viewTagsLevel} onChange={(e) => setViewTagsLevel(Number(e.target.value))}>
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* View Discussions Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>View Discussions</Typography>
                    <Chip label={getPermissionLevelName(viewDiscussionsLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.viewDiscussions}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={viewDiscussionsLevel}
                      onChange={(e) => setViewDiscussionsLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* Create Discussions Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>Create Discussions</Typography>
                    <Chip label={getPermissionLevelName(createDiscussionsLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.createDiscussions}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={createDiscussionsLevel}
                      onChange={(e) => setCreateDiscussionsLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Members,
                        GroupPermissionLevel.Moderators,
                        GroupPermissionLevel.Admins,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* Participate in Discussions Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>Participate in Discussions</Typography>
                    <Chip label={getPermissionLevelName(participateInDiscussionsLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.participateInDiscussions}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={participateInDiscussionsLevel}
                      onChange={(e) => setParticipateInDiscussionsLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              {/* Access Chat Permission */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>Access Chat</Typography>
                    <Chip label={getPermissionLevelName(accessChatLevel)} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {permissionDescriptions.accessChat}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <RadioGroup
                      value={accessChatLevel}
                      onChange={(e) => setAccessChatLevel(Number(e.target.value))}
                    >
                      {[
                        GroupPermissionLevel.Anyone,
                        GroupPermissionLevel.MatchingTags,
                        GroupPermissionLevel.Members,
                        GroupPermissionLevel.Moderators,
                        GroupPermissionLevel.Admins,
                        GroupPermissionLevel.Owner,
                      ].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">{getPermissionLevelName(level)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionLevelDescription(level)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>
            </>
          )}

          {/* Tab 4: Location/Geofence */}
          {currentTab === 4 && (
            <>
          {/* Geofence Settings Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon /> Location Restrictions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Limit group membership to users within a specific geographic area
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={geofenceEnabled}
                  onChange={(e) => setGeofenceEnabled(e.target.checked)}
                />
              }
              label="Enable Location Restrictions"
              sx={{ mb: 3 }}
            />

            {geofenceEnabled && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                {errors.geofence && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.geofence}
                  </Alert>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Geofence Center Location
                </Typography>

                {/* Map for selecting geofence center */}
                <Box sx={{ mb: 3 }}>
                  <GeofencePicker
                    center={geofenceCenter}
                    radiusMiles={geofenceRadiusMiles}
                    onChange={setGeofenceCenter}
                    height={400}
                  />
                </Box>

                {/* Display selected coordinates */}
                {geofenceCenter && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Selected location: {geofenceCenter.latitude.toFixed(6)}, {geofenceCenter.longitude.toFixed(6)}
                  </Alert>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Geofence Radius: {geofenceRadiusMiles} miles
                </Typography>
                <Slider
                  value={geofenceRadiusMiles}
                  onChange={(_, value) => setGeofenceRadiusMiles(value as number)}
                  min={1}
                  max={500}
                  marks={[
                    { value: 1, label: '1 mi' },
                    { value: 100, label: '100 mi' },
                    { value: 250, label: '250 mi' },
                    { value: 500, label: '500 mi' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} miles`}
                />

                <Alert severity="info" sx={{ mt: 2 }}>
                  Users must have their home location set within {geofenceRadiusMiles} mile{geofenceRadiusMiles !== 1 ? 's' : ''} of the center to join this group.
                </Alert>
              </Paper>
            )}
          </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={updateGroupMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateGroupMutation.isPending || !name.trim()}
        >
          {updateGroupMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
